import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore.js';
import SetupNotice from '../components/SetupNotice.js';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export default function Login() {
  const navigate = useNavigate();
  const loginStore = useAuthStore((state) => state.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, refresh_token, user } = response.data;
      await loginStore(access_token, refresh_token, user);
      navigate('/home');
    } catch (err: any) {
      const status = err.response?.status;
      const message =
        err.response?.data?.message ||
        (status === 503
          ? 'Set up your database first — see the yellow box above.'
          : err.code === 'ERR_NETWORK'
          ? 'Cannot reach the API. Run: npm run dev:server'
          : 'Login failed. Please check your credentials.');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-margin-mobile select-none">
      <div className="w-full max-w-md bg-[#141414] thin-border rounded-lg p-gutter">
        <div className="text-center mb-8">
          <h1 className="font-headline-xl text-headline-lg text-on-surface mb-2">PVP Academy</h1>
          <p className="text-on-surface-variant text-body-sm">
            Access elite Minecraft combat training.
          </p>
        </div>

        {error && (
          <div className="p-3 mb-6 bg-error/10 border border-error/20 text-error rounded text-[13px] font-medium flex items-center gap-2">
            <span className="material-symbols-outlined text-[16px]">error</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@domain.com"
              required
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface placeholder:text-white/30 outline-none focus:border-error focus:ring-0 transition-all text-body-sm"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface placeholder:text-white/30 outline-none focus:border-error focus:ring-0 transition-all text-body-sm"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-error hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none text-white py-3.5 rounded-[4px] font-button text-button uppercase tracking-wider font-bold transition-all mt-4"
          >
            {isLoading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-on-surface-variant text-[13px] mt-8">
          Don't have an account?{' '}
          <Link to="/register" className="text-error hover:underline font-medium ml-1">
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
}
